import { useParams } from "react-router-dom";
import { REDACTED, useAudit, type AuditEntry } from "@/api/audit";
import {
  EmptyState,
  ErrorNote,
  Loading,
  PageTitle,
  rowsClass,
  secondaryButtonClass,
} from "@/components/ui";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";
import { hasMessage, type MessageKey } from "@/i18n/catalog";
import type { Translate } from "@/i18n/translate";

/**
 * Who changed what (`model.md` §3.11, K-37).
 *
 * The screen a household reaches when two people disagree about what happened.
 * It is deliberately plain: a line per change, newest first, each saying who,
 * what kind of thing, and which fields moved.
 *
 * **It shows less than it could, on purpose.** A field a household typed — a
 * note, a payee, an account's name — is shown as having changed and never as
 * what it changed to, because the trail is kept on a different schedule from the
 * data it describes and copying somebody's words into it would quietly make it a
 * second archive of them.
 */
export function HistoryPage() {
  const { householdId = "" } = useParams();
  const t = useT();
  const format = useFormat();

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAudit(householdId);

  const entries = data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <div>
      <PageTitle lead={t("history.lead")}>{t("history.title")}</PageTitle>

      {isLoading && <Loading />}
      <ErrorNote error={error} />

      {!isLoading && entries.length === 0 && (
        <EmptyState title={t("history.emptyTitle")}>{t("history.emptyBody")}</EmptyState>
      )}

      {entries.length > 0 && (
        <ul className={`mt-6 ${rowsClass}`}>
          {entries.map((entry) => (
            <li key={entry.id} className="py-3" data-testid="history-entry">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <p className="text-sm text-ink">
                  <span className="font-medium">
                    {/* No actor means Kontora itself — a scheduled job, with
                        nobody to name (see tenancy.NewSystemScope). */}
                    {entry.actor?.display_name ?? t("history.system")}
                  </span>{" "}
                  {t(actionKey(entry), { thing: t(entityKey(entry)) })}
                </p>
                <p className="text-xs text-muted" data-testid="history-when">
                  {format.date(entry.occurred_at.slice(0, 10))}
                </p>
              </div>
              <Fields entry={entry} t={t} />
            </li>
          ))}
        </ul>
      )}

      {hasNextPage && (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className={`${secondaryButtonClass} mt-6 min-h-11`}
        >
          {isFetchingNextPage ? t("common.loading") : t("common.loadMore")}
        </button>
      )}
    </div>
  );
}

/** The fields that moved, named but not quoted. */
function Fields({ entry, t }: { entry: AuditEntry; t: Translate }) {
  const fields = Object.keys(entry.diff ?? {});
  if (fields.length === 0) return null;

  return (
    <p className="mt-1 text-xs text-muted" data-testid="history-fields">
      {fields
        .map((field) => {
          const change = entry.diff?.[field];
          const name = t(fieldKey(field));
          // A redacted field says only that it changed. Everything else can say
          // what it changed to, because it is a number, a date or a code.
          if (change?.after === REDACTED || change?.before === REDACTED) {
            return t("history.fieldChanged", { field: name });
          }
          if (change?.before === undefined) {
            return `${name}: ${String(change?.after ?? "")}`;
          }
          return `${name}: ${String(change.before)} → ${String(change.after ?? "")}`;
        })
        .join(" · ")}
    </p>
  );
}

function actionKey(entry: AuditEntry): MessageKey {
  switch (entry.action) {
    case "create":
      return "history.action.create";
    case "delete":
      return "history.action.delete";
    case "restore":
      return "history.action.restore";
    default:
      return "history.action.update";
  }
}

function entityKey(entry: AuditEntry): MessageKey {
  switch (entry.entity_type) {
    case "transfer":
      return "history.entity.transfer";
    case "account":
      return "history.entity.account";
    case "budget":
      return "history.entity.budget";
    case "category":
      return "history.entity.category";
    default:
      return "history.entity.transaction";
  }
}

/**
 * A field's name in the reader's language.
 *
 * The catalogue already names most of them for the import screen's column
 * mapping (K-36), and the rest are added beside those. A field the catalogue has
 * never heard of falls through to its wire name — which is ugly and legible,
 * and better than an empty line where a change should be.
 */
function fieldKey(field: string): MessageKey {
  const key = `auditField.${field}`;
  // A field the catalogue has never heard of falls through to its wire name,
  // which is ugly and legible — and better than an empty line where a change
  // should be.
  return hasMessage(key) ? key : (field as MessageKey);
}
